import { computed } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useSubscriptionStore } from '@/stores/subscriptionAccess'
import { storeToRefs } from 'pinia'
import { DEFAULT_APP_PLATFORM } from '@/const/platformAccess'

/**
 * Composable for reactive role-based checks
 * 
 * Uses the auth store's reactive role state which is kept in sync
 * with the JWT token. This ensures role checks always reflect the
 * current token's role, even after token refresh.
 */
export function useAuth() {
  const authStore = useAuthStore()
  const subscriptionStore = useSubscriptionStore()
  
  // Use storeToRefs to maintain reactivity for computed properties
  const { isGuide, isFellow, isCommon, isMentor, isAdmin, isReferrer } = storeToRefs(authStore)
  const { isInitialized, hasAccess, platforms } = storeToRefs(subscriptionStore)

  /**
   * True for users who receive restricted Tracktalent functionality:
   * - COMMON role users (not enrolled in the program), OR
   * - FELLOW users without `tracktalent` in their `platforms` list.
   *
   * Having a valid subscription alone (`has_access`) is not enough —
   * Tracktalent access requires the platform to be explicitly assigned
   * via `allowed_platforms` on the backend.
   *
   * Until subscription status has loaded, Fellows are not treated as limited
   * (avoids a flash of wrong UI while access is still unknown).
   *
   * Guides, Mentors, Admins are never limited.
   */
  const isLimitedUser = computed(() => {
    if (isCommon.value) return true
    if (isFellow.value) {
      if (!isInitialized.value) return false
      if (platforms.value.includes(DEFAULT_APP_PLATFORM)) return false
      return true
    }
    return false
  })

  return {
    isGuide,
    isCommon,
    isFellow,
    isMentor,
    isReferrer,
    isAdmin,
    isLimitedUser
  }
} 